/**
 * Facet 卡片组件
 * 展示单个场景人格面的状态和信息
 * 
 * 参考：API 文档 8.1 节 Facet 列表响应
 * v9.2 更新：将「上传语料」改为「添加语料」
 */

import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { cn } from '@/shared/lib/utils'
import {
  Eye,
  Rocket,
  Plus,
  Play,
  Loader2,
  Trash2,
  Mic,
  Link2,
} from 'lucide-react'
import type { FacetListItem } from '@/modules/identity/types/facet'
import { FACET_STATUS_CONFIG, SCENE_TAG_CONFIG } from '@/modules/identity/types/facet'

interface FacetCardProps {
  facet: FacetListItem
  onView?: () => void
  onPublish?: () => void
  onLearn?: () => void
  /** v9.2: 添加语料（打开选择弹窗） */
  onAddCorpus?: () => void
  /** v9.2.2: 删除/重置 Facet（回归未学习状态） */
  onDelete?: () => void
  /** v10.2 新增：绑定音色 */
  onBindVoice?: () => void
  isPublishing?: boolean
  isLearning?: boolean
  isDeleting?: boolean
  /** v9.2.1: SSE 实时进度（0-100） */
  sseProgress?: number
  /** v9.2.1: SSE 当前阶段描述 */
  sseStage?: string
  /** v9.2.1: SSE 预计剩余时间 */
  sseEstimatedTime?: string
}

/**
 * Facet 卡片组件
 */
export function FacetCard({
  facet,
  onView,
  onPublish,
  onLearn,
  onAddCorpus,
  onDelete,
  onBindVoice,
  isPublishing = false,
  isLearning = false,
  isDeleting = false,
  sseProgress,
  sseStage,
  sseEstimatedTime,
}: FacetCardProps) {
  const sceneConfig = SCENE_TAG_CONFIG[facet.scene_tag]
  const statusConfig = FACET_STATUS_CONFIG[facet.status]

  const isNotStarted = facet.status === 'not_started'
  const isLearningStatus = facet.status === 'learning'
  const isDraft = facet.status === 'draft'
  const isActive = facet.status === 'active'
  const isFailed = facet.status === 'failed'

  return (
    <div
      className={cn(
        'group relative border rounded-xl p-4 transition-all duration-200 min-h-[220px] flex flex-col',
        'hover:shadow-md hover:border-violet-200',
        isNotStarted && 'bg-slate-50/50 border-dashed',
        isLearningStatus && 'border-blue-200 bg-blue-50/30',
        isDraft && 'border-gray-200',
        isActive && 'border-emerald-200 bg-emerald-50/30',
        isFailed && 'border-red-200 bg-red-50/30',
      )}
    >
      {/* 场景图标 */}
      <div className="flex items-start justify-between mb-3">
        <div
          className={cn(
            'text-3xl p-2 rounded-lg',
            isNotStarted ? 'bg-slate-100' : 'bg-white shadow-sm'
          )}
        >
          {sceneConfig?.icon || '📋'}
        </div>
        {/* 状态徽章 + 删除按钮 */}
        <div className="flex items-center gap-2">
          <Badge
            variant="secondary"
            className={cn(
              'text-xs',
              statusConfig?.bgColor,
              statusConfig?.color
            )}
          >
            {statusConfig?.icon} {statusConfig?.label}
          </Badge>
          {/* 已学习的场景显示删除按钮（草稿、已激活、失败） */}
          {(isDraft || isActive || isFailed) && onDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-slate-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => {
                e.stopPropagation()
                onDelete()
              }}
              disabled={isDeleting}
              title="删除（重置为未学习状态）"
            >
              {isDeleting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
            </Button>
          )}
        </div>
      </div>

      {/* 场景名称 */}
      <h3 className="font-medium text-slate-800 mb-1">
        {facet.scene_name || sceneConfig?.label}
      </h3>

      {/* 版本信息（已学习的才显示） */}
      {facet.version && (
        <p className="text-xs text-slate-500 mb-2">
          v{facet.version}
        </p>
      )}

      {/* 学习进度条（学习中状态）- 使用 SSE 实时进度 */}
      {isLearningStatus && (
        <div className="mb-3 bg-blue-50 border border-blue-100 rounded-lg p-2.5">
          {/* 进度百分比 + 阶段描述 */}
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-blue-600 flex items-center gap-1 truncate flex-1 mr-2">
              <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
              <span className="truncate">
                {sseStage || '学习中'}
              </span>
            </span>
            <span className="text-blue-500 font-mono flex-shrink-0">
              {/* 优先使用 SSE 进度（即使是 0 也显示），避免 fallback 到旧数据 */}
              {sseProgress !== undefined
                ? `${sseProgress}%`
                : '处理中...'}
            </span>
          </div>
          {/* 进度条 */}
          <div className="h-1.5 bg-blue-100 rounded-full overflow-hidden">
            {/* SSE 进度存在时显示实际进度（包括 0%） */}
            {sseProgress !== undefined ? (
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-300"
                style={{ width: `${Math.max(sseProgress, 2)}%` }}  // 最小显示 2% 宽度，避免看不见
              />
            ) : (
              /* 无 SSE 进度时显示条纹动画 */
              <div 
                className="h-full w-full rounded-full"
                style={{
                  background: 'repeating-linear-gradient(90deg, #3b82f6 0%, #60a5fa 50%, #3b82f6 100%)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 1.5s infinite linear',
                }}
              />
            )}
          </div>
          {/* 预计剩余时间 */}
          {sseEstimatedTime && (
            <p className="text-xs text-blue-400 mt-1.5 text-right">
              预计剩余: {sseEstimatedTime}
            </p>
          )}
        </div>
      )}

      {/* 统计信息（v9.2 更新：显示已关联语料列表） */}
      <div className="space-y-1.5 text-sm text-slate-500">
        <div className="flex items-center gap-2">
          <span>📝 语料:</span>
          <span className="font-medium text-slate-700">
            {facet.corpus_list?.length || facet.corpus_count || 0} 份
            {facet.total_item_count ? ` (${facet.total_item_count} 条)` : ''}
          </span>
        </div>
        {/* 显示已关联的语料名称 */}
        {facet.corpus_list && facet.corpus_list.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {facet.corpus_list.slice(0, 3).map(c => (
              <Badge key={c.corpus_id} variant="outline" className="text-xs font-normal">
                {c.name}
              </Badge>
            ))}
            {facet.corpus_list.length > 3 && (
              <span className="text-xs text-slate-400">
                +{facet.corpus_list.length - 3}
              </span>
            )}
          </div>
        )}
        {facet.test_pass_rate !== null && (
          <div className="flex items-center gap-2">
            <span>🧪 测试:</span>
            <span
              className={cn(
                'font-medium',
                facet.test_pass_rate >= 0.9
                  ? 'text-emerald-600'
                  : facet.test_pass_rate >= 0.7
                  ? 'text-amber-600'
                  : 'text-red-600'
              )}
            >
              {Math.round(facet.test_pass_rate * 100)}%
            </span>
          </div>
        )}
        {/* v10.2 更新：显示绑定音色信息（不论是否已发布） */}
        {(isDraft || isActive || isFailed) && (
          <div className="flex items-center gap-2">
            <Mic className="h-3 w-3 text-slate-400" />
            <span>音色:</span>
            {facet.voice_binding ? (
              <span className="font-medium text-slate-700">
                {facet.voice_binding.voice_card_name}
              </span>
            ) : facet.voice_name ? (
              // 向后兼容：使用旧字段
              <span className="font-medium text-slate-700">
                {facet.voice_name}
              </span>
            ) : (
              // 未绑定
              <div className="flex items-center gap-2">
                <span className="text-slate-400">未绑定</span>
                {onBindVoice && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-5 text-xs px-2"
                    onClick={(e) => {
                      e.stopPropagation()
                      onBindVoice()
                    }}
                  >
                    <Link2 className="h-3 w-3 mr-1" />
                    绑定音色
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
        {/* v10.1: 显示合成参数（已绑定时） */}
        {isActive && (facet.voice_binding || facet.voice_config) && (
          <div className="text-xs text-slate-400 ml-5">
            语速 {(facet.voice_binding?.speech_rate || facet.voice_config?.speech_rate)?.toFixed(1) || '1.0'} | 
            音调 {(facet.voice_binding?.pitch_rate || facet.voice_config?.pitch_rate)?.toFixed(1) || '1.0'} | 
            音量 {facet.voice_binding?.volume || facet.voice_config?.volume || 50}
          </div>
        )}
        {isNotStarted && (!facet.corpus_list || facet.corpus_list.length === 0) && (
          <p className="text-slate-400">暂无语料</p>
        )}
      </div>

      {/* 弹性空间：撑开中间区域，使按钮固定在底部 */}
      <div className="flex-1 min-h-4" />

      {/* 操作按钮（v9.2 更新：支持添加语料和学习）- 固定在底部 */}
      <div className="flex items-center gap-2">
        {/* 未开始 + 无语料：只显示添加语料 */}
        {isNotStarted && (facet.corpus_list?.length || facet.corpus_count || 0) === 0 && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={onAddCorpus}
          >
            <Plus className="h-4 w-4 mr-2" />
            添加语料
          </Button>
        )}

        {/* 未开始 + 有语料：显示添加语料 + 学习 */}
        {isNotStarted && (facet.corpus_list?.length || facet.corpus_count || 0) > 0 && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={onAddCorpus}
              title="添加语料"
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              className="flex-1"
              onClick={onLearn}
              disabled={isLearning}
            >
              {isLearning ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              学习
            </Button>
          </>
        )}

        {/* 学习中：禁用的等待按钮 */}
        {isLearningStatus && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            disabled
          >
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            学习中...
          </Button>
        )}

        {/* 草稿：查看、添加语料、学习、发布 */}
        {isDraft && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={onView}
              title="查看"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onAddCorpus}
              title="添加语料"
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onLearn}
              disabled={isLearning || (facet.corpus_list?.length || facet.corpus_count || 0) === 0}
              title="重新学习"
            >
              {isLearning ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
            </Button>
            <Button
              size="sm"
              className="flex-1"
              onClick={onPublish}
              disabled={isPublishing}
            >
              {isPublishing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Rocket className="h-4 w-4 mr-2" />
              )}
              发布
            </Button>
          </>
        )}

        {/* 失败：查看、添加语料、重新学习 */}
        {isFailed && (
          <>
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={onView}
            >
              <Eye className="h-4 w-4 mr-2" />
              查看
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onAddCorpus}
              title="添加语料"
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              className="flex-1"
              onClick={onLearn}
              disabled={isLearning || (facet.corpus_list?.length || facet.corpus_count || 0) === 0}
            >
              {isLearning ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              重试
            </Button>
          </>
        )}

        {/* 已激活（已发布）：查看、添加语料、重新学习、重新发布 */}
        {isActive && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={onView}
              title="查看"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onAddCorpus}
              title="添加语料"
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onLearn}
              disabled={isLearning || (facet.corpus_list?.length || facet.corpus_count || 0) === 0}
              title="重新学习"
            >
              {isLearning ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
            </Button>
            <Button
              size="sm"
              className="flex-1"
              onClick={onPublish}
              disabled={isPublishing}
              title="更新发布"
            >
              {isPublishing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Rocket className="h-4 w-4 mr-2" />
              )}
              发布
            </Button>
          </>
        )}
      </div>

      {/* 条纹动画样式 */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  )
}

/**
 * Facet 卡片骨架屏
 */
export function FacetCardSkeleton() {
  return (
    <div className="border rounded-xl p-4 animate-pulse">
      <div className="flex items-start justify-between mb-3">
        <div className="w-12 h-12 bg-slate-200 rounded-lg" />
        <div className="w-16 h-5 bg-slate-200 rounded-full" />
      </div>
      <div className="w-24 h-5 bg-slate-200 rounded mb-2" />
      <div className="space-y-2 mb-4">
        <div className="w-20 h-4 bg-slate-200 rounded" />
        <div className="w-16 h-4 bg-slate-200 rounded" />
      </div>
      <div className="w-full h-8 bg-slate-200 rounded" />
    </div>
  )
}
